/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *

/**
 * Description Read More Toggle (Works for Brand & Default Collections)
 */
const initReadMore = () => {
  const containers = document.querySelectorAll('.js-read-more');
  
  containers.forEach(container => {
    // Find the closest wrapper that contains the toggle
    // Support for default collection (.collection-description-wrapper) 
    // and brand collection (.collection-brand__info)
    const parent = container.closest('.collection-description-wrapper') || container.closest('.collection-brand__info');
    if (!parent) return;
    
    const toggle = parent.querySelector('.js-read-more-toggle');
    if (!toggle) return;

    // Initial check for height
    const thresholdHeight = container.closest('.collection-brand') ? 120 : 72; // Different thresholds for different designs
    
    if (container.scrollHeight > thresholdHeight + 5) {
      toggle.style.display = 'inline-flex';
      container.classList.add('is-clamped');
      if (container.closest('.collection-brand')) {
        container.style.maxHeight = thresholdHeight + 'px';
      }
    } else {
      toggle.style.display = 'none';
      container.classList.remove('is-clamped');
      container.style.maxHeight = 'none';
    }

    // Toggle click
    toggle.onclick = (e) => {
      e.preventDefault();
      const isExpanded = container.classList.toggle('is-expanded');
      toggle.classList.toggle('is-active');
      toggle.setAttribute('aria-expanded', isExpanded);
      
      if (isExpanded) {
        container.style.maxHeight = container.scrollHeight + 'px';
      } else {
        container.style.maxHeight = thresholdHeight + 'px';
      }
    };
  });
};

/**
 * Mobile Sidebar Menu Toggle (Specialized for Brand collections)
 */
const initBrandMenu = () => {
  const sidebarToggles = document.querySelectorAll('.sidebar-menu-toggle');
  
  sidebarToggles.forEach(toggle => {
    const parent = toggle.closest('.collection-brand__menu');
    if (!parent) return;
    
    const menu = parent.querySelector('.brand-menu');
    if (!menu) return;

    toggle.onclick = (e) => {
      e.preventDefault();
      const isExpanded = menu.style.display === 'block';
      
      if (isExpanded) {
        menu.style.display = 'none';
        toggle.classList.remove('is-active');
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        menu.style.display = 'block';
        toggle.classList.add('is-active');
        toggle.setAttribute('aria-expanded', 'true');
      }
    };
  });
};

const initBrandFeatures = () => {
  initReadMore();
  initBrandMenu();
};

// Initialize on DOMContentLoaded and also when Shopify sections are reloaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBrandFeatures);
} else {
  initBrandFeatures();
}

document.addEventListener('shopify:section:load', initBrandFeatures);
window.addEventListener('resize', initBrandFeatures);




/**
 * High-Value Enquiry Flow & Cart Protection
 */
const initEnquiryFlow = () => {
  const modalOverlay = document.getElementById('enquiry-modal-overlay');
  const closeBtn = document.querySelector('.enquiry-close');
  const openBtns = document.querySelectorAll('.js-native-enquire');
  const atcContainer = document.querySelector('.quantity-submit-row__submit');
  const spbContainer = document.querySelector('.product-dynamic-payment-button');

  // Open modal via Event Delegation (handles elements dynamically added by variant changes)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.js-native-enquire');
    if (btn) {
      e.preventDefault();
      const overlay = document.getElementById('enquiry-modal-overlay');
      if (overlay) overlay.style.display = 'flex';
    }
  });

  // Close modal
  if (closeBtn && modalOverlay) {
    closeBtn.addEventListener('click', () => {
      modalOverlay.style.display = 'none';
    });
    // Close on overlay click
    modalOverlay.addEventListener('click', (e) => {
      if (e.target === modalOverlay) {
        modalOverlay.style.display = 'none';
      }
    });
  }

  // Monitor variant changes to enforce price threshold logic and update sku dynamically
  document.addEventListener('change', (e) => {
    // This looks for variant changes via inputs or selects.
    // The theme handles its own JS, but we want a passive observer.
    setTimeout(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');
      if (!variantId) return;

      // We can check if the current variant string in form indicates a price change,
      // but without access to the variant JSON directly, we enforce the server-side
      // block. The simplest cart protection is blocking the ATC submit action if a high value variant is somehow chosen.
      // E.g., if we were to fetch variant details here we could hide the button dynamically.
    }, 50);
  });
  
  // Hard Cart Protection
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
  forms.forEach(form => {
    form.addEventListener('submit', (e) => {
      // If the Enquire button is visible on this page, do not allow ATC no matter what.
      const enquireBtnVisible = document.querySelector('.js-native-enquire');
      if (enquireBtnVisible && window.getComputedStyle(enquireBtnVisible).display !== 'none') {
        const atcBtnHiddenStr = atcContainer ? window.getComputedStyle(atcContainer).display : 'block';
        if (!atcContainer || atcBtnHiddenStr === 'none' || !document.querySelector('.add-to-cart')) {
           e.preventDefault();
           e.stopImmediatePropagation();
           modalOverlay.style.display = 'flex'; // Trigger enquire
           return false;
        }
      }
    });
  });


    // Dynamic "Make an enquiry" link logic next to "More payment options"
  const target = document.querySelector('.product-dynamic-payment-button');
  const staticWrapper = document.querySelector('.static-enquiry-link-wrapper');

  const checkEnquiryLinkState = () => {
    if (!target || window.getComputedStyle(target).display === 'none') {
      if (staticWrapper) staticWrapper.style.display = 'block';
      return;
    }

    const moreOptionsBtn = target.querySelector('.shopify-payment-button__more-options');
    if (moreOptionsBtn) {
      if (staticWrapper) staticWrapper.style.display = 'none';

      if (!target.querySelector('.custom-enquiry-link')) {
        const divider = document.createElement('span');
        divider.className = 'custom-enquiry-divider';
        divider.innerHTML = ' | ';
        divider.style.margin = '0 8px';
        divider.style.opacity = '0.5';
        divider.style.color = 'inherit';
        
        const link = document.createElement('a');
        link.href = '#';
        link.className = 'js-native-enquire custom-enquiry-link';
        link.innerText = 'Make an enquiry';
        link.style.cursor = 'pointer';
        link.style.textDecoration = 'underline';
        link.style.fontSize = '14px';
        link.style.padding  = "0 10%";
        link.style.color = '#2b2f2e';
        
        moreOptionsBtn.parentNode.insertBefore(divider, moreOptionsBtn.nextSibling);
        divider.parentNode.insertBefore(link, divider.nextSibling);
      }
    } else {
      const shopifyBtn = target.querySelector('.shopify-payment-button');
      if (shopifyBtn) {
        if (staticWrapper) staticWrapper.style.display = 'block';
      }
    }
  };

  if (target) {
    checkEnquiryLinkState();
    
    const observer = new MutationObserver(() => {
      checkEnquiryLinkState();
    });
    observer.observe(target, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });

    // Extra fallback check after 3 seconds in case Shopify loads late
    setTimeout(checkEnquiryLinkState, 3000);
  } else {
    if (staticWrapper) staticWrapper.style.display = 'block';
  }

    // Listen to variant change event to update the hidden SKU and pre-written message in the modal
  document.addEventListener('on:variant:change', (e) => {
    const variant = e.detail.variant;
    if (!variant) return;

    // Update SKU hidden input
    const skuInput = document.querySelector('.js-enquire-sku');
    if (skuInput) {
      skuInput.value = variant.sku || '';
    }

    // Update textarea message dynamically
    const textarea = document.getElementById('enquiryMessage');
    if (textarea) {
      const productTitle = textarea.dataset.productTitle || '';
      const productUrl = textarea.dataset.productUrl || '';
      const newSku = variant.sku || '';
      
      textarea.value = `I am interested in ${productTitle}.\nSKU: ${newSku}\nLink: ${productUrl}`;
    }
  });

};




const initProductEnhancements = () => {
  // 1. Tabs Switching Logic
  const tabLinks = document.querySelectorAll('.tabs-nav__item a');
  const tabPanes = document.querySelectorAll('.tab-pane');

  tabLinks.forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const targetId = link.getAttribute('href').replace('#', '');
      
      // Update active nav
      tabLinks.forEach(l => l.parentElement.classList.remove('active'));
      link.parentElement.classList.add('active');
      
      // Update active pane
      tabPanes.forEach(pane => {
        pane.style.display = 'none';
        pane.classList.remove('active');
      });
      const targetPane = document.getElementById(targetId);
      if (targetPane) {
        targetPane.style.display = 'block';
        targetPane.classList.add('active');
      }
    });
  });

      
// 2. Delivery Countdown Logic
  const countdownEl = document.querySelector('.js-delivery-countdown');
  const deliveryBlock = document.getElementById('delivery');
  if (countdownEl && deliveryBlock) {
    const cutoffStr = deliveryBlock.getAttribute('data-cutoff') || '16:00';
    const [cutoffH, cutoffM] = cutoffStr.split(':').map(Number);

    const updateCountdown = () => {
      const now = new Date();
      const cutoff = new Date();
      cutoff.setHours(cutoffH, cutoffM, 0, 0);

      let diff = cutoff - now;
      let arrivalDate = new Date();
      const dayOfWeek = now.getDay(); // 0 (Sun) to 6 (Sat)
      const deliveryTextSpan = document.querySelector('.js-delivery-text');
      
      const isSaturdayEnabled = deliveryBlock.getAttribute('data-saturday') === 'true';
      const isHolidayMode = deliveryBlock.getAttribute('data-holiday-mode') === 'true';
      const holidayOffset = parseInt(deliveryBlock.getAttribute('data-holiday-offset')) || 0;

      if (diff > 0 && dayOfWeek >= 1 && dayOfWeek <= 5) {
        // Weekday before cutoff
        if (dayOfWeek === 5 && !isSaturdayEnabled) {
          // Friday before 4pm, but Saturday delivery is disabled
          arrivalDate.setDate(now.getDate() + 3); // Monday
        } else {
          arrivalDate.setDate(now.getDate() + 1); // Tomorrow (Tue, Wed, Thu, Fri, or Sat)
        }
        
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (countdownEl) {
          countdownEl.innerHTML = `${hours} hour${hours !== 1 ? 's' : ''} and ${mins} minute${mins !== 1 ? 's' : ''}`;
        }
        
        if (deliveryTextSpan) {
          deliveryTextSpan.innerHTML = `Order within <span class="js-delivery-countdown">${hours} hour${hours !== 1 ? 's' : ''} and ${mins} minute${mins !== 1 ? 's' : ''}</span> for <strong>Next Day Delivery</strong> on <span class="js-delivery-date">...</span>`;
        }
        deliveryBlock.style.opacity = '1';
      } else {
        // After cutoff or Weekend
        deliveryBlock.style.opacity = '1';

        if (dayOfWeek === 5) { // Friday after cutoff
          arrivalDate.setDate(now.getDate() + 4); // Tuesday
        } else if (dayOfWeek === 6) { // Saturday
          arrivalDate.setDate(now.getDate() + 3); // Tuesday
        } else if (dayOfWeek === 0) { // Sunday
          arrivalDate.setDate(now.getDate() + 2); // Tuesday
        } else if (diff <= 0) { // Mon-Thurs after cutoff
          arrivalDate.setDate(now.getDate() + 2); // Day after tomorrow (e.g. Wednesday -> Friday)
        }

        if (deliveryTextSpan) {
          deliveryTextSpan.innerHTML = `Order now for <strong>Next Day Delivery</strong> on <span class="js-delivery-date">...</span>`;
        }
      }

      // Apply Holiday Offset if enabled
      if (isHolidayMode && holidayOffset > 0) {
        arrivalDate.setDate(arrivalDate.getDate() + holidayOffset);
      }

      // Format delivery date: "Wednesday 29th April"
      const dateEl = document.querySelector('.js-delivery-date');
      if (dateEl) {
        const day = arrivalDate.getDate();
        let suffix = 'th';
        if (day % 10 === 1 && day !== 11) suffix = 'st';
        else if (day % 10 === 2 && day !== 12) suffix = 'nd';
        else if (day % 10 === 3 && day !== 13) suffix = 'rd';
        
        const weekday = arrivalDate.toLocaleDateString('en-GB', { weekday: 'long' });
        const month = arrivalDate.toLocaleDateString('en-GB', { month: 'long' });
        dateEl.innerHTML = `${weekday} ${day}${suffix} ${month}`;
      }
    };

    updateCountdown();
    setInterval(updateCountdown, 60000);
  }

  // 3. Image Protection (Anti-Theft)
  const protectedImages = document.querySelectorAll('.product-gallery img, .product-media img');
  protectedImages.forEach(img => {
    img.addEventListener('contextmenu', (e) => e.preventDefault());
    img.addEventListener('dragstart', (e) => e.preventDefault());
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initEnquiryFlow();
  initProductEnhancements();
});
document.addEventListener('shopify:section:load', () => {
  initEnquiryFlow();
  initProductEnhancements();
});
